import { type CreatePullRequestOption, type PullRequest } from "gitea-js";
import { type ActorRefFrom, assign, fromPromise, log, sendTo, setup } from "xstate";

import { docsApi } from "@moment/app/state/api/docsapi";

import { type GitDiff, type GitMergeAnalysis } from "~/data/git";

import { type gitRepositoryMachine } from "../repository/git-repository.machine";
import { type GitRepositoryMachineEmitted } from "../repository/git-repository.types";

export const gitMergeMachine = setup({
	types: {
		context: {} as {
			gitRepositoryActorRef: ActorRefFrom<typeof gitRepositoryMachine>;
			workspaceName: string;
			documentId: string;
			mergeRequests: PullRequest[];
			sourceBranch?: string;
			targetBranch?: string;
			mergeAnalysis?: GitMergeAnalysis;
			mergeError?: string;
			diff?: GitDiff;
			selectedMergeRequestId?: number;
		},
		input: {} as {
			gitRepositoryActorRef: ActorRefFrom<typeof gitRepositoryMachine>;
			workspaceName: string;
			documentId: string;
		},
		events: {} as
			| GitRepositoryMachineEmitted
			| {
					type: "merge.validate";
					payload: {
						sourceBranch?: string;
						targetBranch?: string;
					};
			  }
			| { type: "merge.execute" }
			| {
					type: "mergeRequest.fetch";
			  }
			| {
					type: "mergeRequest.create";
			  }
			| {
					type: "mergeRequest.approve";
					payload?: {
						mergeRequestId: number;
					};
			  }
			| {
					type: "mergeRequest.decline";
					payload?: {
						mergeRequestId: number;
					};
			  }
			| {
					type: "mergeRequest.close";
					payload?: {
						mergeRequestId: number;
					};
			  },
	},
	actors: {
		fetchMergeRequests: fromPromise<
			PullRequest[],
			{
				workspaceName: string;
				documentId: string;
			}
		>(async ({ input }) => {
			return docsApi.document.mergeRequest.list(input.workspaceName, input.documentId);
		}),

		createMergeRequest: fromPromise<
			PullRequest,
			{
				workspaceName: string;
				documentId: string;
				options: CreatePullRequestOption;
			}
		>(async ({ input }) => {
			return docsApi.document.mergeRequest.create(
				input.workspaceName,
				input.documentId,
				input.options
			);
		}),

		reviewMergeRequest: fromPromise<
			void,
			{
				workspaceName: string;
				documentId: string;
				mergeRequestId: number;
				event: Parameters<typeof docsApi.document.mergeRequest.addReview>[3]["event"];
			}
		>(async ({ input }) => {
			await docsApi.document.mergeRequest.addReview(
				input.workspaceName,
				input.documentId,
				input.mergeRequestId,
				{
					event: input.event,
				}
			);
		}),

		closeMergeRequest: fromPromise<
			void,
			{
				workspaceName: string;
				documentId: string;
				mergeRequestId: number;
			}
		>(async ({ input }) => {
			await docsApi.document.mergeRequest.close(
				input.workspaceName,
				input.documentId,
				input.mergeRequestId
			);
		}),
	},
}).createMachine({
	id: "git-merge",
	context: ({ input }) => ({
		gitRepositoryActorRef: input.gitRepositoryActorRef,
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		mergeRequests: [],
	}),
	type: "parallel",
	states: {
		listing: {
			initial: "idle",
			states: {
				idle: {
					on: {
						"mergeRequest.fetch": {
							guard: ({ context }) => !!context.workspaceName && !!context.documentId,
							target: "fetchingMergeRequests",
						},
					},
				},

				fetchingMergeRequests: {
					invoke: {
						src: "fetchMergeRequests",
						input: ({ context }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
						}),
						onDone: {
							actions: assign(({ event }) => ({
								mergeRequests: event.output as PullRequest[],
							})),
							target: "done",
						},
						onError: {
							actions: log(({ event }) => event),
							target: "done",
						},
					},
				},

				done: {},
			},
		},

		viewing: {
			initial: "empty",
			states: {
				empty: {
					description: "User is selecting a source and target branch",
				},

				error: {
					description:
						"User has selected a source and target branch but there was an error",
				},

				validating: {
					description: "User has selected a source and target branch",
					initial: "idle",
					states: {
						idle: {
							always: [
								{
									guard: ({ context }) =>
										!!context.sourceBranch && !!context.targetBranch,
									target: "fetchingSource",
								},
								{
									target: "#git-merge.viewing.empty",
								},
							],
						},

						fetchingSource: {
							description:
								"Fetching the source branch to make sure we're up to date with remote",
							entry: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.fetch",
									payload: {
										branch: context.sourceBranch,
									},
									sender: self,
								})
							),
							on: {
								"git.fetch.done": {
									target: "fetchingTarget",
								},
								"git.fetch.error": {
									actions: log(({ event }) => event),
									target: "#git-merge.viewing.error",
								},
							},
						},

						fetchingTarget: {
							description:
								"Fetching the target branch to make sure we're up to date with remote",
							entry: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.fetch",
									payload: {
										branch: context.targetBranch,
									},
									sender: self,
								})
							),
							on: {
								"git.fetch.done": {
									target: "analyzing",
								},
								"git.fetch.error": {
									actions: log(({ event }) => event),
									target: "#git-merge.viewing.error",
								},
							},
						},

						analyzing: {
							description: "Analyzing if the branches can be merged cleanly",
							entry: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.branch.analyze",
									payload: {
										source: context.sourceBranch,
										target: context.targetBranch,
									},
									sender: self,
								})
							),
							on: {
								"git.branch.analyze.done": {
									actions: assign(({ event }) => ({
										mergeAnalysis: event.output as GitMergeAnalysis,
									})),
									target: "diffing",
								},
								"git.branch.analyze.error": {
									actions: assign(({ event }) => ({
										mergeError: JSON.stringify(event.error),
									})),
									target: "#git-merge.viewing.unmergeable",
								},
							},
						},

						diffing: {
							description: "Diffing the branches to see the changes",
							entry: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.diff.refs",
									payload: {
										aSide: context.targetBranch,
										bSide: context.sourceBranch,
									},
									sender: self,
								})
							),
							on: {
								"git.diff.refs.done": {
									actions: assign(({ event }) =>
										event.type === "git.diff.refs.done"
											? {
													diff: event.output as GitDiff,
												}
											: {}
									),
									target: "#git-merge.viewing.mergeable",
								},
								"git.diff.refs.error": {
									actions: assign(({ event }) => ({
										mergeError: JSON.stringify(event.error),
									})),
									target: "#git-merge.viewing.unmergeable",
								},
							},
						},
					},
				},

				mergeable: {
					description:
						"Target and source branches have been validated and diffed and are ready to be merged",
					on: {
						"mergeRequest.create": {
							target: "#git-merge.creating.creatingMergeRequest",
						},
						"merge.execute": {
							actions: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.branch.merge",
									payload: {
										source: context.sourceBranch,
										target: context.targetBranch,
										deleteSource: true,
									},
									sender: self,
								})
							),
							on: {
								"git.branch.merge.error": {
									// TODO: create an error state
									actions: log(({ event }) => event),
									target: "#git-merge.viewing.unmergeable",
								},
								"git.branch.merge.done": {
									actions: assign({
										sourceBranch: undefined,
										targetBranch: undefined,
										mergeAnalysis: undefined,
										mergeError: undefined,
										diff: undefined,
									}),
									target: "#git-merge.viewing.empty",
								},
							},
						},
						"mergeRequest.approve": {
							target: ["#git-merge.reviewing.approving", "#git-merge.viewing"],
						},
						"mergeRequest.decline": {
							target: ["#git-merge.reviewing.declining", "#git-merge.viewing"],
						},
						"mergeRequest.close": {
							target: ["#git-merge.reviewing.closing", "#git-merge.viewing"],
						},
					},
				},

				unmergeable: {
					description: "Target and source branches can not be merged cleanly",
					meta: {
						message: "The target and source branches can not be merged cleanly",
					},
				},
			},

			on: {
				"merge.validate": {
					actions: assign(({ event }) => ({
						sourceBranch: event.payload.sourceBranch ?? undefined,
						targetBranch: event.payload.targetBranch ?? undefined,
						// Reset fields that will be set via validation
						diff: undefined,
						mergeAnalysis: undefined,
						mergeError: undefined,
					})),
					target: "#git-merge.viewing.validating",
				},
			},
		},

		creating: {
			initial: "idle",
			states: {
				idle: {
					description: "Waiting for a merge request creation to be requested",
				},

				error: {
					description: "There was an error creating the merge request",
				},

				creatingMergeRequest: {
					initial: "pushingSource",

					states: {
						pushingSource: {
							entry: sendTo(
								({ context }) => context.gitRepositoryActorRef,
								// @ts-ignore self is an ActorRef but sender does not like it
								({ context, self }) => ({
									type: "git.push",
									payload: {
										branch: context.sourceBranch,
									},
									sender: self,
								})
							),
							on: {
								"git.push.done": {
									target: "creating",
								},
								"git.push.error": {
									actions: log(({ event }) => event),
									target: "#git-merge.creating.error",
								},
							},
						},

						creating: {
							invoke: {
								src: "createMergeRequest",
								input: ({ context }) => ({
									workspaceName: context.workspaceName ?? "",
									documentId: context.documentId ?? "",
									options: {
										base: context.targetBranch ?? "",
										head: context.sourceBranch ?? "",
										title: `Merge \`${context.sourceBranch}\` into \`${context.targetBranch}\``,
									},
								}),
								onDone: {
									actions: assign(({ context, event }) => ({
										mergeRequests: [
											...context.mergeRequests,
											event.output as PullRequest,
										],
									})),
									target: "#git-merge.creating.done",
								},
								onError: {
									actions: log(({ event }) => event),
									target: "#git-merge.viewing.unmergeable",
								},
							},
						},
					},
				},

				done: {
					type: "final",
				},
			},
		},

		reviewing: {
			initial: "idle",
			states: {
				idle: {
					description: "Waiting for a review action to be requested",
				},

				error: {
					description: "There was an error reviewing the merge request",
				},

				approving: {
					invoke: {
						src: "reviewMergeRequest",
						input: ({ context, event }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							mergeRequestId:
								event.type === "mergeRequest.approve" &&
								event.payload?.mergeRequestId
									? event.payload.mergeRequestId
									: (context.mergeRequests.find(
											(mr) =>
												mr.head?.ref === context.sourceBranch &&
												mr.base?.ref === context.targetBranch
										)?.id ?? 0),
							event: "APPROVED",
						}),
						onDone: {
							target: "#git-merge.reviewing.done",
						},
						onError: {
							actions: log(({ event }) => event),
							target: "#git-merge.reviewing.error",
						},
					},
				},

				declining: {
					invoke: {
						src: "reviewMergeRequest",
						input: ({ context, event }) => ({
							workspaceName: context.workspaceName,
							documentId: context.documentId,
							mergeRequestId:
								event.type === "mergeRequest.decline" &&
								event.payload?.mergeRequestId
									? event.payload.mergeRequestId
									: (context.mergeRequests.find(
											(mr) =>
												mr.head?.ref === context.sourceBranch &&
												mr.base?.ref === context.targetBranch
										)?.id ?? 0),
							event: "REQUEST_CHANGES",
						}),
						onDone: {
							target: "#git-merge.reviewing.done",
						},
						onError: {
							actions: log(({ event }) => event),
							target: "#git-merge.reviewing.error",
						},
					},
				},

				closing: {
					invoke: {
						src: "closeMergeRequest",
						input: ({ context, event }) => {
							const mergeRequestId =
								event.type === "mergeRequest.close" && event.payload?.mergeRequestId
									? event.payload.mergeRequestId
									: (context.mergeRequests.find(
											(mr) =>
												mr.head?.ref === context.sourceBranch &&
												mr.base?.ref === context.targetBranch
										)?.id ?? 0);
							return {
								workspaceName: context.workspaceName,
								documentId: context.documentId,
								mergeRequestId,
							};
						},
						onDone: {
							actions: assign(({ context }) => {
								const mergeRequestId = context.mergeRequests.find(
									(mr) =>
										mr.head?.ref === context.sourceBranch &&
										mr.base?.ref === context.targetBranch
								)?.id;
								return {
									mergeRequests: context.mergeRequests.filter(
										(mr) => mr.id !== mergeRequestId
									),
								};
							}),
							target: "#git-merge.reviewing.done",
						},
						onError: {
							actions: log(({ event }) => event),
							target: "#git-merge.reviewing.error",
						},
					},
				},

				done: {
					type: "final",
				},
			},
		},
	},
});
